/**
 * The MUI presentation atom, wrapped by TanStack `createLink` so `to` / `params`
 * / `search` are checked against the registered route tree.
 *
 *   <Link {...appLinks.salesOrder({ slug })} label={number} />   typed route
 *   <Link href="https://…" target="_blank" label="Docs" />       raw href
 *   <Link component="button" onClick={…} label="Void" />         no destination
 *          │                                     │
 *          └─ the last two render straight through MUI, so they need
 *             NO router context (Storybook, bare component tests).
 */
import { Link as MuiLink, type LinkProps as MuiLinkProps } from '@mui/material';
import { createLink, type LinkComponentProps } from '@tanstack/react-router';
import type { AnyRouter, RegisteredRouter } from '@tanstack/router-core';
import { type CSSProperties, forwardRef, type ReactElement, type ReactNode } from 'react';

type LinkPresentationProps = Omit<MuiLinkProps, 'children'> & {
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  // MUI v9 dropped system props from Link; the atom keeps its own size default.
  fontSize?: CSSProperties['fontSize'];
};

const LinkPresentation = forwardRef<HTMLAnchorElement, LinkPresentationProps>(function LinkPresentation(
  { label, icon, disabled: _disabled, sx = {}, fontSize = '14px', ...linkProps },
  ref
) {
  return (
    <MuiLink
      ref={ref}
      {...linkProps}
      sx={{
        textDecoration: 'underline',
        textUnderlineOffset: '4px',
        cursor: 'pointer',
        fontSize,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ...sx,
      }}
    >
      {label}
      {icon}
    </MuiLink>
  );
});

const RoutedLink = createLink(LinkPresentation);

/**
 * `TTo` defaults to `'.'` rather than TanStack's `undefined`, which is what
 * makes `to` optional: this atom also renders destination-less links.
 */
type LinkComponent = <
  TRouter extends AnyRouter = RegisteredRouter,
  const TFrom extends string = string,
  const TTo extends string | undefined = '.',
  const TMaskFrom extends string = TFrom,
  const TMaskTo extends string = '',
>(
  props: LinkComponentProps<typeof LinkPresentation, TRouter, TFrom, TTo, TMaskFrom, TMaskTo>
) => ReactElement;

const Link: LinkComponent = (props) => {
  if (props.to !== undefined) return <RoutedLink {...props} />;

  // Without a route destination there is no router work to do, so the
  // navigation-only options are dropped rather than leaked onto the DOM node.
  const {
    activeOptions: _activeOptions,
    activeProps: _activeProps,
    children: _children,
    from: _from,
    hash: _hash,
    hashScrollIntoView: _hashScrollIntoView,
    ignoreBlocker: _ignoreBlocker,
    inactiveProps: _inactiveProps,
    mask: _mask,
    params: _params,
    preload: _preload,
    preloadDelay: _preloadDelay,
    preloadIntentProximity: _preloadIntentProximity,
    reloadDocument: _reloadDocument,
    replace: _replace,
    resetScroll: _resetScroll,
    search: _search,
    startTransition: _startTransition,
    state: _state,
    to: _to,
    unsafeRelative: _unsafeRelative,
    viewTransition: _viewTransition,
    ...presentationProps
  } = props;

  return <LinkPresentation {...presentationProps} />;
};

export default Link;
